import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { initializeFaceTecSDK } from '../services/faceTecService';
import { EnrollmentProcessor } from '../processors/EnrollmentProcessor';

export function Home() {
  const { loginWithRedirect, user } = useAuth0();
  const { isAuth0Authenticated, isFullyAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [enrolling, setEnrolling] = useState(false);
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (!loading) {
      if (isFullyAuthenticated) {
        navigate('/dashboard');
      } else if (isAuth0Authenticated) {
        navigate('/mfa-verification');
      }
    }
  }, [isAuth0Authenticated, isFullyAuthenticated, loading, navigate]);
  
  const handleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        scope: 'openid profile email offline_access'
      }
    });
  };

  const handleEnrollFace = async () => {
    try {
      setEnrolling(true);
      setEnrollmentStatus(null);
      
      // Initialize FaceTec if needed
      await initializeFaceTecSDK();
      
      // Get session token (reuse existing function)
      const faceTecApp = await import('../services/faceTecService');
      faceTecApp.faceTecApp.getSessionToken((sessionToken) => {
        if (sessionToken) {
          // Use test user ID for development
          const testUserId = user?.sub || "e0cb165d-8495-463f-9cc8-04eed4af738e";
          
          const enrollmentProcessor = new EnrollmentProcessor(sessionToken, testUserId, (result) => {
            console.log('Enrollment result:', result);
            
            if (result.success) {
              setEnrollmentStatus('success');
            } else {
              setEnrollmentStatus('failed');
            }
            setEnrolling(false);
          });
        } else {
          console.error('Failed to get session token');
          setEnrolling(false);
          setEnrollmentStatus('failed');
        }
      });
      
    } catch (error) {
      console.error('Enrollment failed:', error);
      setEnrolling(false);
      setEnrollmentStatus('failed');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8 text-center">
        <h1 className="text-3xl font-bold mb-6">FaceTec MFA Test App</h1>
        <p className="mb-8 text-gray-600">
          This app demonstrates two-factor authentication using Auth0 and FaceTec biometric verification.
        </p>
        
        <div className="space-y-4">
          <button
            onClick={handleLogin}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Sign In with Auth0
          </button>
          
          <button
            onClick={handleEnrollFace}
            disabled={enrolling}
            className="w-full py-3 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {enrolling ? 'Enrolling Face...' : 'Enroll Face (Test)'}
          </button>
        </div>
        
        {enrollmentStatus && (
          <div className={`mt-4 p-3 rounded-md ${
            enrollmentStatus === 'success' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {enrollmentStatus === 'success' 
              ? '✅ Face enrolled successfully!' 
              : '❌ Enrollment failed. Please try again.'}
          </div>
        )}
      </div>
    </div>
  );
}